import { useAppSelector } from "../../../hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import Loading from "../../../Components/Loading";
import { CRM } from "../../../Types/CRM";
import { logEvent } from "firebase/analytics";
import { analytics } from "../../../firebase";
import { Button, Card, CardBody, Container } from "reactstrap";
import { useLocation } from "react-router-dom";
import { Add, CheckBox } from "@mui/icons-material";
import { SwipeableList, SwipeableListItem, SwipeAction, TrailingActions, Type as SwipeableType } from "react-swipeable-list";
import 'react-swipeable-list/dist/styles.css';
import Swal from "sweetalert2";
import 'sweetalert2/src/sweetalert2.scss';
import { useInfiniteScroll } from "../../../Hooks/useInfiniteScroll";
import CRMEntryModal from "./CRMEntryModal";

type Tab = 'Tab1' | 'Tab2';

type CloseActionPayload = {
    id: string,
    message: string;
    transactionId: string | undefined;
}

const CRMActions = () => {
    const location = useLocation();
    const item = location.state
    const custID = item.id;
    const token = useAppSelector((state) => state.auth.token);
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': btoa(`Bearer ${token}`),
    }
    const tab1Ref = useRef(null);
    const tab2Ref = useRef(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [crmData, setCrmData] = useState<CRM []>([]);
    const [actionData, setActionData] = useState<CRM []>([]);
    const [activeTab, setActiveTab] = useState<Tab>('Tab1');
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [currentCRMIndex, setCurrentCRMIndex] = useState(0);
    const [currentActionIndex, setCurrentActionIndex] = useState(0);
    const [displayedCRMData, setDisplayedCRMData] = useState<CRM []>([]);
    const [displayedActionData, setDisplayedActionData] = useState<CRM []>([]);
    const [crmLoadingMore, setCrmLoadingMore] = useState<boolean>(false);
    const [actionLoadingMore, setActionLoadingMore] = useState<boolean>(false);
    const itemsPerLoad = 10;

    useEffect(() => {
        setDisplayedCRMData(crmData.slice(0, itemsPerLoad));
        setCurrentCRMIndex(itemsPerLoad);
    }, [crmData]);

    useEffect(() => {
        setDisplayedActionData(actionData.slice(0, itemsPerLoad));
        setCurrentActionIndex(itemsPerLoad);
    }, [actionData]);

    useEffect(() => {
        if (activeTab === 'Tab1') {
            setDisplayedCRMData(crmData.slice(0, itemsPerLoad));
            setCurrentCRMIndex(itemsPerLoad);
        } else {
            setDisplayedActionData(actionData.slice(0, itemsPerLoad));
            setCurrentActionIndex(itemsPerLoad);
        }
    }, [activeTab]);

    const ScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const loadMoreCRMData = () => {
        if (crmLoadingMore || currentCRMIndex >= crmData.length) return;

        setCrmLoadingMore(true);
        const nextIndex = currentCRMIndex + itemsPerLoad;
        const newItems = crmData.slice(currentCRMIndex, nextIndex);

        setDisplayedCRMData(prevData => [...prevData, ...newItems]);
        setCurrentCRMIndex(nextIndex);
        setCrmLoadingMore(false);
    };

    const loadMoreActionData = () => {
        if (actionLoadingMore || currentActionIndex >= actionData.length) return;

        setActionLoadingMore(true);
        const nextIndex = currentActionIndex + itemsPerLoad;
        const newItems = actionData.slice(currentActionIndex, nextIndex);

        setDisplayedActionData(prevData => [...prevData, ...newItems]);
        setCurrentActionIndex(nextIndex);
        setActionLoadingMore(false);
    };

    const onClose = () => {
        setModalOpen(false);
        onRefresh();
    }

    const onRefresh = useCallback(() => {
        getData();
    }, [crmData, actionData])

    useEffect(() => {
        getData();

    }, []);

    const getData = async () => {
        const crmUrl = 'https://customer.hirsch.com/account/v2/employee/customer/crm';
        const actionUrl = 'https://customer.hirsch.com/account/v2/employee/customer/crm/action';

        try {
            setLoading(true);

            const [crmRes, actionRes] = await Promise.all([
                fetch(`${crmUrl}?id=${item.id}`, { headers }),
                fetch(`${actionUrl}?id=${item.id}`, { headers })
            ]);

            const crmJson = await crmRes.json();
            const actionJson = await actionRes.json();

            setCrmData(crmJson.notes);
            setActionData(
                [...actionJson.actions].sort((a, b) =>
                    b.status.localeCompare(a.status)
                )
            );

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        };
    };

    const closeAction = async ({
        id, 
        message, 
        transactionId
    }: CloseActionPayload
    ) => {
        const alertMessage = 'Are you sure you want to close this action?'

        Swal.fire({
            title: 'Close Action',
            text: alertMessage,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            confirmButtonColor: 'limegreen',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                logEvent(analytics, 'close_action_web', {
                    name: 'Close CRM Action',
                    description: 'User closed CRM Action',
                    transactionId: transactionId,
                });

                try {
                    const baseUrl = 'https://customer.hirsch.com/account/v2/employee/customer/crm/action';
        
                    const res = await fetch(baseUrl, {
                        method: 'PUT',
                        headers,
                        body: JSON.stringify({
                            id: id,
                            message: message,
                            transactionId: transactionId
                        })
                    })
        
                    const json = await res.json();
                    console.log('CRM JSON:', json);

                    if (json.errorMessage) {
                        Swal.showValidationMessage(`Action failed: ${json.errorMessage}`);
                        return false; 
                    }

                    return json;

                } catch (err) {
                    Swal.showValidationMessage(`Request Failed: ${err}`)
                    console.error(err);
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
            if (result.isConfirmed) {
                onRefresh();
                Swal.fire({
                    title: 'Closed!',
                    text: 'Your Action has been closed.',
                    icon: 'success',
                    confirmButtonColor: '#f1b434',
                });
            }
        });
    };

    const renderCrmItem = ({ item }: { item: CRM }) => (
        <Card className="shadow mb-1">
            <CardBody>
                <div style={{ display: 'flex' }}>
                    <span className='itemName'>Date:</span>
                    <span style={{ left: 5 }}>{item.date}</span>
                </div>
                <div style={{ paddingTop: 10, display: 'flex' }}>
                    <span style={{ flexWrap: 'wrap' }}>{item.text}</span>
                </div>
            </CardBody>
        </Card>
    );

    const renderActionItem = ({ item }: { item: CRM }) => {

        const RightAction = () => {

            return (
                <TrailingActions>
                    <SwipeAction
                        onClick={() => closeAction({
                            id: custID, 
                            message: item.text1, 
                            transactionId: item.transactionId,
                        })
                    }
                    >
                        <div 
                            style={{ 
                                display: 'flex',
                                width: 100,
                                backgroundColor: 'green', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                            }}
                        >
                            <CheckBox
                                sx={{ color: 'white', }}
                            />
                        </div>
                    </SwipeAction>
                </TrailingActions>
            )
        }

        return (
            <div className="text-start">
                {item.status === '1' ? (

                    <SwipeableList
                        type={SwipeableType.IOS}
                    >
                        <SwipeableListItem
                            key={item.transactionId}
                            trailingActions={RightAction()}
                        >
                            <Card style={{ width: '100%', userSelect: 'none' }}>
                                <CardBody>
                                    <div style={{ flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                                        <div className="circle-dot bg-warning"></div>
                                        <span style={{ fontWeight: 'bold', paddingLeft: 5 }}>{item.status === '1' ? 'Open' : 'Closed'}</span>
                                    </div>
                                    <div style={{ paddingBlock: 10, }}>
                                        {item.text1 && <span>{item.text1}</span>}
                                        {item.text2 && <span>{item.text2}</span>}
                                    </div>
                                    <div style={{ flexDirection: 'row', justifyContent: 'flex-start', paddingTop: 5 }}>
                                        <span className="itemName">Open Date:</span>
                                        <span style={{ paddingLeft: 5 }}>{item.date}</span>
                                        {item.closeDate &&
                                            <div style={{ flexDirection: 'row' }}>
                                                <span className="itemName">Close Date:</span>
                                                <span style={{ paddingLeft: 5 }}>{item.closeDate}</span>
                                            </div>
                                        }
                                    </div>
                                </CardBody>
                            </Card>
                        </SwipeableListItem>
                    </SwipeableList>
                ) : (
                    <Card
                        style={{ flex: 1, justifyContent: 'space-around' }}
                    >
                        <CardBody>
                            <div style={{ flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                                <div className="circle-dot bg-success"></div>
                                <span style={{ fontWeight: 'bold', paddingLeft: 5 }}>{item.status === 'O' ? 'Open' : 'Closed'}</span>
                            </div>
                            <div style={{ marginBlock: 10 }}>
                                {item.text1 && <span>{item.text1}</span>}
                                {item.text2 && <span>{item.text2}</span>}
                            </div>
                            <div style={{ flexDirection: 'row', paddingTop: 5, justifyContent: 'space-between' }}>
                                <div style={{ flexDirection: 'row', justifyContent: 'flex-start' }}>
                                    <span className="itemName">Open Date:</span>
                                    <span style={{ paddingLeft: 5 }}>{item.date}</span>
                                </div>
                                <div style={{ flexDirection: 'row', justifyContent: 'flex-start', }}>
                                    {item.closeDate &&
                                        <div style={{ flexDirection: 'row' }}>
                                            <span className="itemName">Close Date:</span>
                                            <span style={{ paddingLeft: 5 }}>{item.closeDate}</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )
                }
            </div>
        )
    };

    const crmSentinelRef = useInfiniteScroll({
        onEndReached: loadMoreCRMData,
        enabled: !loading,
    });

    const actionSentinelRef = useInfiniteScroll({
        onEndReached: loadMoreActionData,
        enabled: !loading,
    });

    const tabContent = () => {
        switch (activeTab) {
            case 'Tab1':
                return (
                    <div 
                        ref={tab1Ref}
                        style={{ flex: 1 }}
                    >
                        {!displayedCRMData.length
                        ? (
                            <div>
                                <h3>No CRM Notes</h3>
                            </div>
                        )
                        : displayedCRMData.map((item) => (
                            renderCrmItem({ item })
                        ))}
                        <div 
                            ref={crmSentinelRef}
                            style={{
                                minHeight: 20,
                                width: '100%',
                            }}
                        />
                    </div>
                )

            case 'Tab2':
                return (
                    <div 
                        ref={tab2Ref}
                        style={{ flex: 1 }}
                    >
                        {!displayedCRMData.length
                        ? (
                            <div>
                                <h3>No CRM Actions</h3>
                            </div>
                        )
                        : displayedActionData.map((item) => (
                            renderActionItem({ item })
                        ))}
                        <div 
                            ref={actionSentinelRef}
                            style={{
                                minHeight: 20,
                                width: '100%',
                            }}
                        />
                    </div>
                )

        };
    };

    const handleActionTab = async () => {
        setActiveTab('Tab2');
        ScrollToTop();
        logEvent(analytics, 'view_actions_webs', {
            name: 'View Action Tab',
            description: 'User viewed CRM Action report'
        });
    };

    const handleCrmModal = async () => {
        setModalOpen(true);
        logEvent(analytics, 'crm_modal_web', {
            name: 'Customer CRM Modal',
            description: 'User used Customer CRM Modal'
        });
    }

    return (
        <div className="bg-light w-100">
            <div 
                style={{ 
                    flexDirection: 'row', 
                    textAlign: 'start',
                    paddingBlockStart: '10px',
                    paddingBlockEnd: '20px',
                    position: 'sticky',
                    top: 60,
                    zIndex: 1000,
                }}
                className="bg-light w-100"
            >
                <Container>
                    <Button
                        className="mainButton w-25 fw-bold"
                        onClick={handleCrmModal}
                    >
                        <Add />
                        <span className="itemName">New CRM</span>
                    </Button>
                </Container>
            </div>
            {loading ? (<Loading />)
                : (
                    <div>
                        <div
                            style={{
                                position: 'sticky',
                                top: 132,
                                zIndex: 1000,
                                paddingBlockEnd: '20px',
                            }}
                            className="bg-light w-100"
                        >
                            <Container className="d-flex">
                                <Button
                                    className={activeTab === 'Tab1' ? `tabButton bg-primary` : `tabButton bg-white`}
                                    onClick={() => {
                                        setActiveTab('Tab1');
                                        ScrollToTop();
                                    }}
                                >
                                    <span style={{ fontWeight: 'bold' }}>Notes</span>
                                </Button>
                                <Button
                                    className={activeTab === 'Tab2' ? `tabButton bg-primary` : `tabButton bg-white`}
                                    onClick={handleActionTab}
                                >
                                    <span style={{ fontWeight: 'bold' }}>Actions</span>
                                </Button>
                            </Container>
                        </div>
                        <Container>
                            {tabContent()}
                        </Container>
                    </div>
                )
            }
            {modalOpen &&
                <CRMEntryModal
                    visible={modalOpen}
                    onClose={onClose}
                    customerID={item.id}
                />
            }
        </div>
    )
};

export default CRMActions;